import { useEffect, useState } from 'react';

import { appParams } from '@playbooks/api';
import { Feedback } from '@playbooks/molecules/feedbacks';
import { SearchForm } from '@playbooks/molecules/forms';
import { Pagination } from '@playbooks/molecules/paginations';
import { Button } from '@playbooks/ui/components/button';
import { DialogFooter, DialogHeader, DialogTitle } from '@playbooks/ui/components/dialog';

import { useStore } from 'contexts';

const WorkspaceMcpFlowSelect = ({ selected, setSelected, canManageServers, onClose, onAdd, onEdit, onSave }) => {
	const [mcps, setMcps] = useState([]);
	const [loading, setLoading] = useState(false);
	const [params, setParams] = useState<any>({ ...appParams, pageSize: 10 });
	const [meta, setMeta] = useState({ page: 0, pageSize: 10, totalRecords: 0 });
	const [error, setError] = useState('');
	const store = useStore();

	useEffect(() => {
		let active = true;
		setLoading(true);
		setError('');
		store
			.query({ url: '/workspace/mcps', params })
			.then(async response => {
				if (!active) return;
				const records = response.data || [];
				setMcps(records);
				setMeta(response.meta || { page: params.page, pageSize: params.pageSize, totalRecords: 0 });
				if (!records.length) return;
				const health = await store.createRecord({
					url: '/workspace/mcps/health',
					data: { ids: records.map(item => item.id) },
				});
				if (active) setMcps(health.data || records);
			})
			.catch(nextError => active && setError(nextError?.message || 'MCPs could not be loaded.'))
			.finally(() => active && setLoading(false));
		return () => {
			active = false;
		};
	}, [params, store]);

	const onSelect = mcp => {
		const active = selected.some(item => item.id === mcp.id);
		if (!active && mcp.health !== 'available') return;
		setSelected(
			selected.some(item => item.id === mcp.id) ? selected.filter(item => item.id !== mcp.id) : [...selected, mcp],
		);
	};
	const onDelete = async mcp => {
		if (!window.confirm(`Remove ${mcp.name} from this workspace?`)) return;
		await store.deleteRecord({ url: `/workspace/mcps/${mcp.uuid}` });
		setMcps(current => current.filter(item => item.id !== mcp.id));
		setSelected(current => current.filter(item => item.id !== mcp.id));
	};

	return (
		<>
			<DialogHeader>
				<DialogTitle>Choose MCPs</DialogTitle>
			</DialogHeader>
			<div className='flex-between gap-3 border-b p-4'>
				<SearchForm
					id='agent_mcp_flow_search'
					query={params.query || ''}
					setQuery={query => setParams({ ...params, page: 0, query })}
					placeholder='Search MCPs...'
					size='default'
					className='grow'
				/>
				<Button size='default' disabled={!canManageServers} onClick={onAdd}>
					Add New
				</Button>
			</div>
			<div className='min-h-96 py-2'>
				{loading ? (
					<Feedback
						layout='modal'
						icon='spinner'
						title='Loading MCPs'
						text='Checking workspace MCP servers.'
						className='min-h-80'
					/>
				) : error ? (
					<Feedback
						layout='modal'
						icon='triangle-exclamation'
						title='MCPs unavailable'
						text={error}
						className='min-h-80'
					/>
				) : mcps.length ? (
					<div className='grid gap-1'>
						{mcps.map(mcp => (
							<div key={mcp.id} className='flex items-center gap-1'>
								<Button
									variant='ghost'
									data-active={selected.some(item => item.id === mcp.id) || undefined}
									disabled={mcp.health !== 'available' && !selected.some(item => item.id === mcp.id)}
									onClick={() => onSelect(mcp)}
									className='h-auto min-h-12 min-w-0 grow justify-start px-3 py-2 text-left'>
									<span className='min-w-0 grow'>
										<span className='block truncate text-sm font-medium'>{mcp.name}</span>
										<span className='block truncate text-xs font-normal text-foreground-body'>
											{mcp.description || mcp.url}
										</span>
										<span className='block text-xs text-foreground-subtle'>
											{mcp.health === 'available'
												? 'Available'
												: mcp.health === 'unavailable'
													? 'Unavailable'
													: 'Not checked'}
										</span>
									</span>
								</Button>
								<Button variant='ghost' size='sm' disabled={!canManageServers} onClick={() => onEdit(mcp)}>
									Edit
								</Button>
								<Button variant='ghost' size='sm' disabled={!canManageServers} onClick={() => onDelete(mcp)}>
									Remove
								</Button>
							</div>
						))}
					</div>
				) : (
					<Feedback
						layout='modal'
						icon='server'
						title={params.query ? 'No MCPs found' : 'No MCP servers yet'}
						text={params.query ? 'Try a different search.' : 'Add an MCP server to make tools available to agents.'}
						className='min-h-80'
					/>
				)}
			</div>
			<div className='border-t p-3'>
				<Pagination
					layout='table'
					meta={meta}
					params={params}
					setParams={setParams}
					taskRunning={loading}
					totalLabel='MCPs'
				/>
			</div>
			<DialogFooter>
				<Button variant='secondary' onClick={onClose}>
					Close
				</Button>
				<Button onClick={onSave}>Save</Button>
			</DialogFooter>
		</>
	);
};

export { WorkspaceMcpFlowSelect };
